import React, { useCallback, useEffect, useState } from "react";
import ActivityFeed from "./ActivityFeed";
import { fetchSocialFeed } from "./communitySocialApi";

/**
 * Self-fetching Activity Feed section for public pages.
 * Pass contextType + id (creator_id / organization_id / project_id).
 */
function ActivityFeedSection({
  contextType = "global",
  creatorId = null,
  organizationId = null,
  projectId = null,
  title = "Activity",
  perPage = 15,
  className = "",
  headingId = "activity-feed-heading",
}) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => {
    setReloadKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      const feed = await fetchSocialFeed({
        context: contextType,
        creator_id: creatorId,
        organization_id: organizationId,
        project_id: projectId,
        per_page: perPage,
        // Public page feeds stay chronological — ranking is Social-global only.
        mode: "chronological",
      });
      if (cancelled) return;
      setPosts(feed.posts || []);
      setError(!!feed.error);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [contextType, creatorId, organizationId, projectId, perPage, reloadKey]);

  const handleItemUpdate = useCallback((updated) => {
    if (updated && updated._deleted) {
      setPosts((prev) => prev.filter((p) => String(p.id) !== String(updated.id)));
      return;
    }
    setPosts((prev) =>
      prev.map((p) => (String(p.id) === String(updated.id) ? updated : p))
    );
  }, []);

  return (
    <section
      className={`activity-feed-section ${className}`.trim()}
      aria-labelledby={headingId}
    >
      <h2 id={headingId} className="activity-feed-section__heading">
        {title}
      </h2>
      <ActivityFeed
        contextType={contextType}
        loading={loading}
        error={error}
        items={posts}
        onRetry={reload}
        onItemUpdate={handleItemUpdate}
      />
    </section>
  );
}

export default ActivityFeedSection;
